import { useState, type FC } from 'react'
import { DEFAULT_PAGE_SIZE } from '../../../common/constants'
import { Button } from '../../../components/Button/Button'
import { LinkButton } from '../../../components/Button/LinkButton'
import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { Spinner } from '../../../components/Spinner/Spinner'
import { BookingStatus, type SchedulingFilters, type Booking, type SchedulingEventType } from '../../../models/Scheduling'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useCancelBookingMutation, useGetBookingsQuery, useGetSchedulingEventTypesQuery } from '../../../store/scheduling/scheduling'
import { addToast } from '../../../store/toast/toastSlice'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { formatSuggestedTime } from '../bookingFormat'
import { BookingRow } from '../rows/BookingRow'
import { getSourceMeetingIds, pastMeetingPath } from '../shared/bookingNavigation'
import { ContactChips } from '../rows/ContactChips'
import { SchedulingEmptyState } from './SchedulingEmptyState'

const SuggestedSummary: FC<{ booking: Booking; eventType?: SchedulingEventType }> = ({ booking, eventType }) => (
  <div className='d-flex flex-column flex-grow-1 mw-0 gap-2'>
    <div className='bold'>{booking.title ?? eventType?.name}</div>
    {booking.clientMessage ? <div className='paragraph-small zocks-text-gray-700 text-truncate'>{booking.clientMessage}</div> : null}
    <div className='d-flex align-items-center flex-wrap gap-3 paragraph-small zocks-text-gray-700'>
      <span className='d-inline-flex align-items-center zocks-gap-0.5'>
        <i className='bi bi-person' aria-hidden='true' />
        <ContactChips clients={booking.clients} />
      </span>
      <span className='d-inline-flex align-items-center zocks-gap-0.5 text-nowrap'>
        <i className='bi bi-calendar-week' aria-hidden='true' />
        {formatSuggestedTime({ start: booking.start, end: booking.end, suggestedDate: booking.suggestedDate })}
      </span>
      {eventType ? (
        <span className='d-inline-flex align-items-center zocks-gap-0.5'>
          <i className='bi bi-stars' aria-hidden='true' />
          {eventType.name}
        </span>
      ) : null}
    </div>
  </div>
)

export const SuggestedTab: FC<{ filters: SchedulingFilters }> = ({ filters }) => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const { data: bookingsResponse, isFetching } = useGetBookingsQuery({
    ...filters,
    accountId: authenticatedUser.accountId,
    status: BookingStatus.OPPORTUNITY,
    pageRequest: { page: page - 1, size: pageSize },
  })
  const bookings = bookingsResponse?.data
  const totalElements = bookingsResponse?.page.totalElements ?? 0
  const { data: eventTypes } = useGetSchedulingEventTypesQuery({ accountId: authenticatedUser.accountId })
  const [cancelBooking, { isLoading: isCancelling, originalArgs }] = useCancelBookingMutation()

  if (isFetching) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (!Array.isArray(bookings) || bookings.length === 0) {
    return <SchedulingEmptyState title='No suggestions' subtitle='Suggestions appear when your AI Assistant spots a meeting to book.' />
  }

  const eventTypeById = new Map((eventTypes ?? []).map((eventType) => [eventType.id, eventType]))

  const handleDismiss = async (booking: Booking) => {
    const bookingId = booking.bookingId
    const result = await cancelBooking({ accountId: authenticatedUser.accountId, payload: { bookingId } })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not dismiss the suggestion', messages: ['Please try again.'] }))
    } else {
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_SUGGESTION_DISMISSED, eventDetails: { bookingId } })
      dispatch(addToast({ variant: 'success', title: 'Suggestion dismissed', messages: [`${booking.title ?? 'The suggestion'} was dismissed.`] }))
    }
  }

  return (
    <div className='d-flex flex-column gap-2'>
      {bookings.map((booking) => {
        const eventType = booking.eventTypeId ? eventTypeById.get(booking.eventTypeId) : undefined
        const sourceMeetingIds = getSourceMeetingIds(booking)
        return (
          <BookingRow key={booking.bookingId} booking={booking} timeBlock={null} summary={<SuggestedSummary booking={booking} eventType={eventType} />}>
            {sourceMeetingIds ? (
              <LinkButton variant='grayButton' to={pastMeetingPath(sourceMeetingIds.roomId, sourceMeetingIds.sessionId)}>
                <i className='bi bi-calendar-event me-1' aria-hidden='true' />
                View source meeting
              </LinkButton>
            ) : null}
            <LinkButton variant='primaryGhost' to={`/scheduling/schedule?opportunityId=${booking.bookingId}`}>
              Schedule
            </LinkButton>
            <Button
              variant='borderlessGrayButton'
              className='p-1'
              aria-label={`Dismiss ${booking.title ?? 'suggestion'}`}
              loading={isCancelling && originalArgs?.payload.bookingId === booking.bookingId}
              disabled={isCancelling}
              onClick={() => handleDismiss(booking)}
            >
              <i className='bi bi-x' aria-hidden='true' />
            </Button>
          </BookingRow>
        )
      })}
      <CustomPaginationComponent
        rowsPerPage={pageSize}
        rowCount={totalElements}
        currentPage={page}
        onChangePage={setPage}
        onChangeRowsPerPage={(newPageSize) => {
          setPage(1)
          setPageSize(newPageSize)
        }}
      />
    </div>
  )
}
